/**
 * PATH: packages/kanban-host/src/CardPeekDrawer.tsx
 * FILE_ID: KANBAN-HOST-CARD-PEEK-001
 * PURPOSE: Standalone in-place CardDetailDrawer mount for non-kanban
 *          surfaces (UAT P2-b — email hub "Open card").
 * WHY: Opening a card FROM the email hub did a full page navigation
 *      (`/kanban/<board>?card=<id>`) — UAT Stream 5: "one drawer
 *      behaviour for 'open card' regardless of origin surface". This
 *      wrapper gives any host page the same overlay drawer the board
 *      uses, fetching the full card + its board's lanes by id, so the
 *      operator reads/edits the card without losing their email
 *      context. Closing returns them exactly where they were.
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────┐   ┌────────────────┐
 *   │ cardId + boardId │ → │ getCard + listLanes │ → │ CardDetailDrawer│
 *   │ (from caller)    │   │ (react-query)       │   │ overlay mount  │
 *   └──────────────────┘   └─────────────────────┘   └────────────────┘
 * DEPENDENCIES:
 *   - @quotech/ui/kanban CardDetailDrawer: the universal drawer.
 *   - ./api getSamKanbanApi: same client every kanban surface uses.
 * RELATED:
 *   - packages/email-hub EmailPreviewPane / SmartActionBar — callers.
 *   - PlannerPage.tsx — the standalone-mount precedent this follows.
 */

import { useQuery } from '@tanstack/react-query'

import { CardDetailDrawer } from '@quotech/ui/kanban'

import { getSamKanbanApi } from './api'

export interface CardPeekDrawerProps {
  /** Card to open; null/undefined renders nothing (drawer closed). */
  cardId: string | null | undefined
  /** The card's board — used to resolve lane names in the drawer.
   *  Optional: without it the drawer still opens, lane labels fall
   *  back to ids. Callers with an allocation summary always have it. */
  boardId?: string | null
  onClose: () => void
}

/**
 * In-place card drawer for non-kanban pages. Mounts the SAME
 * CardDetailDrawer the board view uses (overlay chrome), under the
 * SAME ['kanban','card',id] query key, so a card opened here and
 * later on the board shares one cache entry.
 */
export function CardPeekDrawer({ cardId, boardId, onClose }: CardPeekDrawerProps) {
  const api = getSamKanbanApi()

  const cardQ = useQuery({
    queryKey: ['kanban', 'card', cardId ?? null],
    queryFn: () => api.getCard(cardId!),
    enabled: !!cardId,
    staleTime: 30_000,
  })

  const lanesQ = useQuery({
    queryKey: ['kanban', 'lanes', boardId ?? null],
    queryFn: () => api.listLanes(boardId!),
    enabled: !!cardId && !!boardId,
    staleTime: 60_000,
  })

  if (!cardId || !cardQ.data) return null

  return (
    <CardDetailDrawer
      api={api}
      card={cardQ.data}
      lanes={lanesQ.data ?? []}
      open
      onClose={onClose}
    />
  )
}

export default CardPeekDrawer
